import { useState } from "react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import client from "@/lib/api";
import { toast } from "sonner";

export function DeleteButton({ endpoint, id, label = "record", onDeleted, trigger, recurringSeries = false, scopeOptions = null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const del = async (scope) => {
    setBusy(true);
    try {
      await client.delete(scope ? `${endpoint}/${id}?scope=${scope}` : `${endpoint}/${id}`);
      toast.success(`${label[0].toUpperCase() + label.slice(1)} deleted`);
      onDeleted && onDeleted();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `Failed to delete ${label}`);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-${label}-button`}>
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            {recurringSeries
              ? `This ${label} repeats. Choose which to delete. This cannot be undone.`
              : `This will permanently remove the ${label}. This action cannot be undone.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {recurringSeries ? (
          <div className="flex flex-col gap-2">
            {(scopeOptions || [["this", "Just this task"], ["future", "This and all future tasks"], ["all", "All tasks in the series"]]).map(([k, l]) => (
              <Button key={k} variant="outline" disabled={busy} data-testid={`scope-${k}`} onClick={() => del(k)}>{l}</Button>
            ))}
            <AlertDialogCancel className="mt-1">Cancel</AlertDialogCancel>
          </div>
        ) : (
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); del(); }}
            >
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
